import * as yup from 'yup';
import dayjs from 'dayjs';

import i18n from '@/config/i18n';
import { BaseYup, MAX_INPUT } from '@/config/baseYup';
import { GRADUATION_GROUP_NAME_MAX_LENGTH } from '@/shared/constants/graduation';
import { billingMonthFirstDayStart } from '@/shared/graduation/billingMonth';

export const graduationSearchFormSchema = BaseYup.object({
  keyword: BaseYup.string().max(MAX_INPUT),
  status: BaseYup.string().max(MAX_INPUT).nullable(),
  start_date: BaseYup.date().nullable(),
  end_date: BaseYup.date().nullable(),
});

export const graduationFormSchema = BaseYup.object({
  period_number: BaseYup.number()
    .required(i18n.t('graduation.required.period_number'))
    .transform(function (_value, originalValue) {
      if (typeof originalValue === 'object' && originalValue !== null) {
        return originalValue.value;
      }
      return originalValue;
    })
    .transform((value) => (Number.isNaN(value) ? null : value)),
  learning_start_date: BaseYup.date()
    .required(i18n.t('graduation.required.learning_start_date')),
  due_public_date: BaseYup.date()
    .required(i18n.t('graduation.required.recruitment_start_date'))
    .test(
      'before-trial-end',
      i18n.t('graduation.required.recruitment_before_trial'),
      function (value) {
        const { terms } = this.parent as { terms?: any[] };

        if (!terms || !Array.isArray(terms)) return true;

        const trialDates = terms
          .map((t) => t?.trial_end_date)
          .filter((d) => d && dayjs(d).isValid())
          .map((d) => dayjs(d));

        if (trialDates.length === 0 || !value || !dayjs(value).isValid()) {
          return true;
        }

        const earliestTrial = trialDates.reduce((min, current) =>
          current.isBefore(min, 'day') ? current : min,
        );

        return dayjs(value).isBefore(earliestTrial, 'day');
      },
    ),
  terms: BaseYup.array()
    .of(
      BaseYup.object({
        name: BaseYup.string()
          .required(i18n.t('graduation.required.group_name'))
          .max(
            GRADUATION_GROUP_NAME_MAX_LENGTH,
            i18n.t('graduation.required.group_name_max'),
          ),
        trial_end_date: BaseYup.date()
          .nullable()
          .test(
            'trial-on-or-before-billing-month-first',
            i18n.t('graduation.required.trial_on_or_before_billing_month_first'),
            function (value) {
              const { due_payment_date: duePayment } = this.parent as {
                due_payment_date?: unknown;
              };
              if (!value || !duePayment || !dayjs(value).isValid()) {
                return true;
              }
              const firstOfBilling = billingMonthFirstDayStart(duePayment);
              if (!firstOfBilling) return true;
              return !dayjs(value).startOf('day').isAfter(firstOfBilling, 'day');
            },
          ),
        due_payment_date: BaseYup.date()
          .required(i18n.t('graduation.required.start_date_charging')),
        // ART SHIFT contents granted to the coach of this term.
        // unit_limit === null means 全て (all units of the lesson).
        lessons: BaseYup.array()
          .of(
            BaseYup.object({
              lesson_id: BaseYup.number().required(),
              is_available: BaseYup.boolean().default(false),
              unit_limit: BaseYup.number().nullable().default(null),
              is_shared: BaseYup.boolean().default(false),
            }),
          )
          .optional(),
      }),
    )
    .min(1)
    .test(
      'unique-group-name',
      i18n.t('graduation.required.group_name_unique'),
      function (terms) {
        if (!terms) return true;

        const seen: Record<string, number[]> = {};

        terms.forEach((t: any, index: number) => {
          const name = (t?.name ?? '').trim();
          if (!name) return;
          if (!seen[name]) {
            seen[name] = [];
          }
          seen[name].push(index);
        });

        const duplicatedEntries = Object.values(seen).filter(
          (indexes) => indexes.length > 1,
        );

        if (duplicatedEntries.length === 0) {
          return true;
        }

        const msg = i18n.t('graduation.required.group_name_unique');
        const inner = duplicatedEntries.flatMap((indexes) =>
          indexes.map(
            (idx) =>
              new yup.ValidationError(msg, null, `${this.path}.${idx}.name`),
          ),
        );
        throw new yup.ValidationError(inner);
      },
    ),
  deleted_term_ids: BaseYup.array().of(BaseYup.number()).default([]),
});

export type GraduationFormSearchType = yup.InferType<
  typeof graduationSearchFormSchema
>;
export type GraduationFormType = yup.InferType<typeof graduationFormSchema>;
